import { Body, Button, TextLink } from '@assembly-js/design-system';
import { Editor } from '@tiptap/core';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface Position {
  top: number;
  left: number;
}

interface LinkViewPopoverProps {
  editor: Editor;
  linkUrl: string;
  onEdit: () => void;
  onClose: () => void;
}

export const LinkViewPopover = ({
  editor,
  linkUrl,
  onEdit,
  onClose,
}: LinkViewPopoverProps) => {
  const [position, setPosition] = useState<Position | null>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Calculate position from cursor
  useEffect(() => {
    if (editor && linkUrl) {
      const { from } = editor.state.selection;
      const coords = editor.view.coordsAtPos(from);

      setPosition({
        top: coords.bottom + 8,
        left: coords.left,
      });
    } else {
      setPosition(null);
    }
  }, [editor, linkUrl]);

  // Handle click outside
  useEffect(() => {
    if (!linkUrl) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [linkUrl, onClose]);

  const handleRemove = () => {
    editor.chain().focus().extendMarkRange('link').unsetLink().run();
    onClose();
  };

  const handleEdit = () => {
    onEdit();
  };

  if (!linkUrl || !position) return null;

  // Truncate long URLs for display
  const displayUrl =
    linkUrl.length > 30 ? `${linkUrl.substring(0, 30)}...` : linkUrl;

  return createPortal(
    <div
      ref={popoverRef}
      className="fixed z-50 border border-solid border-secondary-hover shadow-dropdown-menu px-3 py-2 bg-white rounded-md flex items-center gap-3"
      style={{
        top: position.top,
        left: position.left,
      }}
    >
      <span className="flex items-center gap-2">
        <Body size="sm">Go to link:</Body>
        <TextLink
          href={linkUrl}
          target="_blank"
          className="text-sm truncate max-w-48"
        >
          {displayUrl}
        </TextLink>
      </span>
      <div className="h-4 w-px bg-gray-300" />
      <Button variant="text" label="Change" onClick={handleEdit} />
      <div className="h-4 w-px bg-gray-300" />
      <Button variant="text" label="Remove" onClick={handleRemove} />
    </div>,
    document.body,
  );
};
